// The login screen's backdrop: the editor's login map (Content/login.json), slowly panning around its Spawn when its
// settings say so. Built once and kept while the menus are up.
import { Container } from 'pixi.js';
import { app } from '../app';
import { api } from '../api';
import { WorldRenderer } from './renderer';
import { assetUrl } from '../assetUrl';
import type { PlacedEntity } from '../../../shared/src/entityGeometry';

interface LoginMap {
  w: number; h: number; x: number; y: number; chunks: [number, number][]; entities: PlacedEntity[]; dark: boolean; indoors: boolean;
  lights: { x: number; y: number; radius: number; color: number; flicker: boolean }[];
  autoPan: boolean; panSpeed: number; panRadius: number; zoom: number; error?: string;
}

let shared: Backdrop | null = null;

export class Backdrop {
  readonly root = new Container();
  private renderer: WorldRenderer | null = null;
  private info: LoginMap | null = null;

  /** Shows the backdrop (loading it the first time). */
  static show(): void {
    if (!shared) shared = new Backdrop();
    shared.root.visible = true;
  }

  static hide(): void {
    if (shared) shared.root.visible = false;
  }

  static update(dt: number): void {
    if (shared?.root.visible) shared.update(dt);
  }

  private constructor() {
    app.backLayer.addChild(this.root);
    void this.load();
  }

  private async load(): Promise<void> {
    try {
      const info = api.loadLoginMap(await (await fetch(assetUrl('Content/login.json'))).text()) as LoginMap;
      if (info.error) return;
      const r = this.renderer = new WorldRenderer(app.sprites, { chunk: (cx, cy) => { const b = api.backdropChunk(cx, cy); return b.length ? b : null; } });
      for (const l of r.layers) this.root.addChild(l);
      for (const [cx, cy] of info.chunks) r.chunkChanged(cx, cy);
      r.flushChunks(Infinity);
      r.dark = info.dark;
      r.indoors = info.indoors;
      r.mapLights = info.lights ?? [];
      this.info = info;
    } catch (e) {
      console.warn('No login backdrop', e);
    }
  }

  private update(dt: number): void {
    const r = this.renderer, info = this.info;
    if (!r || !info) return;
    // Wander about the Spawn on two slow sine waves (the panning radius in tiles), kept inside the map.
    const t = app.time * info.panSpeed, range = info.autoPan ? info.panRadius * 16 : 0;
    r.zoom = info.zoom || Math.max(2, Math.round(app.height / 270));
    const halfW = app.width / r.zoom / 2, halfH = app.height / r.zoom / 2;
    const clamp = (v: number, half: number, size: number) => size <= half * 2 ? size / 2 : Math.max(half, Math.min(size - half, v));
    r.centerX = clamp(info.x + Math.sin(t * 0.03) * range, halfW, info.w * 16);
    r.centerY = clamp(info.y + Math.sin(t * 0.021 + 1) * range * 0.6, halfH, info.h * 16);
    r.timeOfDay = 0.5;
    r.update(dt);
    r.setResources(info.entities, app.time);
    r.setCharacters([]);
    r.finishLabels();
  }
}
